/* Task-Management Phase 2 / S — saved-filter UI: a small dropdown in the filter
 * bar to save the current board filter, apply a saved one, or delete it. The
 * data ops (fetch/save/apply/delete) + the serialize/restore live on the
 * Workspace instance (state/workspace.ts) and are unit-tested there; this module
 * is the lit rendering half (same detached-host menu grammar as exportMenu /
 * identityMenu). Design gate: light — fits the existing filter-bar grammar.
 *
 * S1: private-per-user (no sharing UI this phase). Confirm-on-overwrite when a
 * name already exists (K-review note 7). */
import { html, nothing, render, type TemplateResult } from 'lit';
import type { Workspace } from '../state/workspace.ts';

/** The "Saved ▾" filter-bar button that opens the saved-filter menu. */
export function savedFilterControl(ws: Workspace): TemplateResult {
  return html`<button class="wsmini wssf-btn" aria-haspopup="menu" title="saved filters"
    @click=${(ev: MouseEvent) => { ev.stopPropagation(); savedFilterMenu(ws, ev.currentTarget as HTMLElement); }}>Saved${ws.savedFilters.length ? html` · ${ws.savedFilters.length}` : nothing}<span class="wscaret" aria-hidden="true">▾</span></button>`;
}

/** The dropdown: the user's saved filters (click to apply, ✕ to delete) + a
 *  "Save current filter…" action. Same detached-host + outside-click + Escape
 *  wiring as exportMenu, through ws._menu/_menuOut/closeMenu. */
export function savedFilterMenu(ws: Workspace, btn: HTMLElement): void {
  ws.closeMenu();
  const m = document.createElement('div');
  m.className = 'wsmenu wssf-menu';
  m.setAttribute('role', 'menu');

  const saveCurrent = async () => {
    const name = await ws.ask({ title: 'Save current filter as…', placeholder: 'e.g. My overdue', ok: 'Save' });
    if (name === null) return;
    const n = name.trim();
    if (!n) return;
    // confirm-on-overwrite (K-review note 7): re-using a name upserts server-side
    if (ws.savedFilters.some((f) => f.name.toLowerCase() === n.toLowerCase())) {
      const ok = await ws.ask({ title: `Overwrite “${n}”?`, hint: 'A saved filter with this name already exists — saving replaces it.', value: n, ok: 'Overwrite' });
      if (ok === null) return;
    }
    await ws.saveCurrentFilter(n);
  };

  render(html`
    ${ws.savedFilters.length
      ? ws.savedFilters.map((f) => html`
        <div class="wssf-row">
          <button role="menuitem" tabindex="-1" class="wssf-apply" title="apply this saved filter"
            @click=${() => { ws.closeMenu(true); ws.applySavedFilter(f.query); }}>${f.name}</button>
          <button class="wssf-del" title="delete this saved filter" aria-label="delete ${f.name}"
            @click=${(ev: MouseEvent) => { ev.stopPropagation(); ws.deleteSavedFilter(f.id); ws.closeMenu(); }}>✕</button>
        </div>`)
      : html`<div class="note">No saved filters yet — set some filters, then save them here.</div>`}
    <div class="wssf-sep"></div>
    <button role="menuitem" tabindex="-1" class="wssf-save"
      @click=${() => { ws.closeMenu(true); saveCurrent(); }}>${ws.ICON.plus}Save current filter…</button>`, m);

  document.body.appendChild(m);
  const r = btn.getBoundingClientRect();
  m.style.left = Math.min(r.left, innerWidth - 240) + 'px';
  m.style.top = (r.bottom + scrollY + 4) + 'px';
  ws._menu = m; ws._menuOwner = btn;
  const items = [...m.querySelectorAll<HTMLElement>('[role="menuitem"]')];
  items.forEach((x, i) => {
    x.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); (items[i + 1] || items[0])?.focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (items[i - 1] || items[items.length - 1])?.focus(); }
      else if (e.key === 'Escape') { e.preventDefault(); ws.closeMenu(true); }
    });
  });
  if (items[0]) items[0].focus();
  ws._menuOut = (ev: MouseEvent) => { if (!m.contains(ev.target as Node) && ev.target !== btn) ws.closeMenu(); };
  setTimeout(() => document.addEventListener('click', ws._menuOut!), 0);
}
